
import { __ } from '@@/bits/intl'
import { $ } from '@@/bits/jquery'
import { SnackbarNotificationType } from '@@/enums'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { computeAndSaveNewPostIndex } from '@@/surface/sorter'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import type { GlobalSnackbarNotificationVuex } from '@@/vuexstore/modules/global_snackbar'
import { PiniaMapAction, PiniaMapState } from 'bits/pinia'
import { Component, Getter, Prop, Vue, Watch, namespace } from 'bits/vue'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfacePostsStore } from 'pinia/surface_posts'
import type { Cid, Id, Post } from 'types'
import SurfacePost from 'vuecomponents/surface_post.vue'
import GridMixin from 'vuemixins/grid_mixin'

const browser = namespace('browser')
const globalSnackbarStore = namespace('globalSnackbar')

@Component({
  components: {
    SurfacePost,
  },
  mixins: [GridMixin],
  setup() {
    useDoubleClickToPost()
  },
})
export default class SurfaceContainerGrid extends Vue {
  $refs!: {
    wishList: HTMLDivElement
  }

  @Prop({ default: () => {} }) reactionIdsByPostId!: Record<Id, Id[]>
  @Prop({ default: () => {} }) commentIdsByPostId!: Record<Id, Id[]>
  @Prop({ default: () => [] }) posts!: Post[]
  @Prop() wallContainer!: HTMLDivElement

  @Getter dir!: 'rtl' | 'ltr'
  @Getter isAPostDragging!: boolean
  @globalSnackbarStore.Action setSnackbar!: (payload: Partial<GlobalSnackbarNotificationVuex>) => void
  @PiniaMapAction(useSurfacePostsStore) shouldDisplayPost!: (postCid: Cid) => boolean
  @PiniaMapState(useSurfaceStore) isPostSizeWide!: boolean
  @PiniaMapState(useSurfaceContainerSizeStore) surfaceContainerWidth!: number
  @namespace('post').Action sortedPost

  initializeGrid!: (
    node: Node,
    savePostHandler: (data: { postCid; sectionId: never; previousCid; nextCid }) => void,
    options: Record<any, any>,
  ) => void // from GridMixin

  debouncedUpdateGrid!: () => void // from GridMixin

  // Called by GridMixin
  onPostRearranged(post: HTMLElement): void {
    // Check if there is a pinned post between unpinned posts or vice versa.
    // If there is, it's an invalid drop and we should revert the post order.
    const $post = $(post)
    const $prevPost = $post.prev('.wish')
    const $nextPost = $post.next('.wish')
    if ($prevPost.length > 0 || $nextPost.length > 0) {
      const isPostPinned = $post.attr('data-pinned') === 'true'
      const isPrevPostPinned = $prevPost.attr('data-pinned') === 'true'
      const isNextPostPinned = $nextPost.attr('data-pinned') === 'true'

      const wasUnpinnedPostDroppedBeforeFirstPinnedPost = !isPostPinned && $prevPost.length === 0 && isNextPostPinned
      const wasPinnedPostDropAfterLastUnpinnedPost = isPostPinned && !isPrevPostPinned && $nextPost.length === 0
      const wasPinnedPostDroppedBetweenUnpinnedPosts = isPostPinned && !isPrevPostPinned && !isNextPostPinned
      const wasUnpinnedPostDroppedBetweenPinnedPosts = !isPostPinned && isPrevPostPinned && isNextPostPinned

      if (
        wasUnpinnedPostDroppedBeforeFirstPinnedPost ||
        wasPinnedPostDropAfterLastUnpinnedPost ||
        wasPinnedPostDroppedBetweenUnpinnedPosts ||
        wasUnpinnedPostDroppedBetweenPinnedPosts
      ) {
        this.resetPostOrder()
        this.showInvalidDragToast(isPostPinned)
        return
      }
    }

    // If the drop is valid, update post sort index as usual.
    computeAndSaveNewPostIndex(post)
  }

  resetPostOrder(): void {
    // After dragging posts with shapeshift (GridMixin), the order is already
    // preserved in the DOM. To revert it, we need to re-render the v-for.
    // There are a few ways to do this but only re-assigning the array passed
    // to v-for seems to not cause a flicker/flash.
    let posts = [...this.internalAllSortedPosts]
    this.$nextTick(() => {
      posts = [...posts.reverse()]
      this.internalAllSortedPosts = posts
      this.$nextTick(() => {
        posts = [...posts.reverse()]
        this.internalAllSortedPosts = posts
      })
    })
  }

  showInvalidDragToast(isPostPinned: boolean): void {
    const message = isPostPinned ? __('Cannot insert between unpinned posts') : __('Cannot insert between pinned posts')
    this.setSnackbar({
      message,
      notificationType: SnackbarNotificationType.error,
    })
  }

  internalAllSortedPosts: Post[] = []

  @Watch('posts')
  handlePostsChange(newPosts: Post[]): void {
    // Avoid updating the internal posts array when dragging cuz it can
    // mess with drag n drop.
    if (!this.isAPostDragging) this.internalAllSortedPosts = [...newPosts]
  }

  beforeMount(): void {
    this.internalAllSortedPosts = [...this.posts]
  }

  mounted(): void {
    this.initializeGrid(
      this.wallContainer as Node,
      (data: { postCid; sectionId: never; previousCid; nextCid }) => {
        this.sortedPost(data)
      },
      { rtl: this.dir === 'rtl' },
    )
  }

  @Watch('surfaceContainerWidth')
  updateGridOnSurfaceContainerWidthChange(): void {
    this.debouncedUpdateGrid()
  }

  get maxPostWidth(): number {
    return this.isPostSizeWide ? 464 : 280
  }

  get postWidth() {
    return this.isPostSizeWide ? this.widePostWidth : this.standardPostWidth
  }

  get widePostWidth(): string {
    if (this.surfaceContainerWidth <= 480) {
      const wishListMargin = 8
      return `calc(100% - ${(2 * wishListMargin) / 1}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 984) {
      const wishListMargin = 8
      return `calc(50% - ${(3 * wishListMargin) / 2}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 1528) {
      const wishListMargin = 16
      return `calc(33.3% - ${(4 * wishListMargin) / 3}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 2008) {
      const wishListMargin = 16
      return `calc(25% - ${(5 * wishListMargin) / 4}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 2488) {
      const wishListMargin = 16
      return `calc(20% - ${(6 * wishListMargin) / 5}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 2968) {
      const wishListMargin = 16
      return `calc(16.67% - ${(7 * wishListMargin) / 6}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 3448) {
      const wishListMargin = 16
      return `calc(14.29% - ${(8 * wishListMargin) / 7}px - 0.5px)`
    } else {
      const wishListMargin = 16
      return `calc(12.5% - ${(9 * wishListMargin) / 7}px - 0.5px)`
    }
  }

  get standardPostWidth(): string {
    if (this.surfaceContainerWidth <= 584) {
      const wishListMargin = 8
      return `calc(50% - ${(3 * wishListMargin) / 2}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 904) {
      const wishListMargin = 8
      return `calc(33.3% - ${(4 * wishListMargin) / 3}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 1272) {
      const wishListMargin = 16
      return `calc(25% - ${(5 * wishListMargin) / 4}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 1568) {
      const wishListMargin = 16
      return `calc(20% - ${(6 * wishListMargin) / 5}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 1864) {
      const wishListMargin = 16
      return `calc(16.67% - ${(7 * wishListMargin) / 6}px - 0.5px)`
    } else if (this.surfaceContainerWidth <= 2160) {
      const wishListMargin = 16
      return `calc(14.29% - ${(8 * wishListMargin) / 7}px - 0.5px)`
    } else {
      const wishListMargin = 16
      return `calc(12.5% - ${(8 * wishListMargin) / 7}px - 0.5px)`
    }
  }
}
